import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { SessionTimeoutWarning } from '@/components/SessionTimeoutWarning'
import { useBehavioralProtection } from '@/hooks/useBehavioralProtection'
import { useSessionTimeout } from '@/hooks/useSessionTimeout'
import { NotificationsModal } from './NotificationsModal'
import { useNotifications } from '@/hooks/useNotifications'
import { NOTIFICATIONS_REFRESH_EVENT } from '@/hooks/useDealSocket'
import { BottomNavigation } from './BottomNavigation'
import { MobileEventLeadCapture } from './MobileEventLeadCapture'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import { useTheme } from '@/hooks/useTheme'
import { useHeader } from '@/contexts/HeaderContext'
import { Bell, Menu } from 'lucide-react'
import { cn } from '@/utils/cn'
import { ThemeModeToggle } from '@/components/ui/ThemeModeToggle'
import { HEADER_BAND_HEIGHT_PX } from '@/constants/layout'
import { getExpandedSidebarLogoFallback } from '@/components/layout/sidebarLogos'
import { resolvePublicAssetUrl } from '@/utils/resolvePublicAssetUrl'

interface DashboardLayoutProps {
  children: React.ReactNode
  title?: string
  actions?: React.ReactNode
}

export function DashboardLayout({ children, title, actions }: DashboardLayoutProps) {
  const navigate = useNavigate()
  const { theme } = useThemeStore()
  const { user, logout } = useAuthStore()
  useBehavioralProtection(Boolean(user), 'audit')
  const rememberEmail =
    typeof window !== 'undefined' &&
    Boolean(localStorage.getItem('crm_secure_remember_email'))

  const handleSessionLogout = useCallback(() => {
    void logout().finally(() => {
      navigate('/login?reason=inactivity', { replace: true })
    })
  }, [logout, navigate])

  const sessionTimeout = useSessionTimeout(handleSessionLogout, !rememberEmail)
  const isMobile = useUIStore((s) => s.isMobile)
  const toggleMobileMenu = useUIStore((s) => s.toggleMobileMenu)
  const syncMobileViewport = useUIStore((s) => s.syncMobileViewport)
  const { isDark, toggleMode } = useTheme()
  const { headerSlot } = useHeader()
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const notifications = useNotifications()

  useEffect(() => {
    syncMobileViewport()
    const mq = window.matchMedia('(max-width: 767px)')
    const onChange = () => syncMobileViewport()
    mq.addEventListener('change', onChange)
    window.addEventListener('resize', onChange)
    return () => {
      mq.removeEventListener('change', onChange)
      window.removeEventListener('resize', onChange)
    }
  }, [syncMobileViewport])

  useEffect(() => {
    const refresh = () => {
      void notifications.refreshUnreadCount()
    }
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, refresh)
    return () => window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, refresh)
  }, [notifications.refreshUnreadCount])

  const initials = (user?.name || 'U')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const getLogoSrc = () =>
    getExpandedSidebarLogoFallback(theme.mode)

  const shellBorder = isDark ? 'border-white/[0.06]' : 'border-[#eef2f7]'

  return (
    <div className='flex h-screen w-full overflow-hidden bg-bg-dark font-sans'>
      <Sidebar />

      <main className='relative z-10 flex min-h-0 min-w-0 flex-1 flex-col bg-bg-main transition-all duration-300'>
        {/* Supertopo — alinhado à faixa do logo (72px) */}
        <header
          className={cn(
            'relative z-30 flex shrink-0 items-center justify-between gap-4 px-4 md:px-8',
            'bg-bg-sidebar pt-[max(12px,env(safe-area-inset-top,0px))] md:border-b md:pt-0',
            shellBorder,
          )}
          style={{ minHeight: HEADER_BAND_HEIGHT_PX }}
        >
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            {/* Mobile only: não usar `flex md:hidden` — `.flex` sobrescreve `md:hidden` no cascade. */}
            {isMobile ? (
              <>
                <button
                  type='button'
                  onClick={toggleMobileMenu}
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors',
                    isDark
                      ? 'text-text-muted hover:bg-white/[0.06] hover:text-text-primary'
                      : 'text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#334155]',
                  )}
                  aria-label='Abrir menu'
                >
                  <Menu size={20} />
                </button>
                <div className='flex flex-1 justify-center'>
                  <img
                    src={getLogoSrc()}
                    alt={theme.companyName}
                    className='h-5 w-auto object-contain opacity-90'
                    onError={(e) => {
                      ;(e.target as HTMLImageElement).src =
                        getExpandedSidebarLogoFallback(theme.mode)
                    }}
                  />
                </div>
              </>
            ) : title ? (
              <h1 className='min-w-0 truncate text-2xl font-bold tracking-tight text-text-primary'>
                {title}
              </h1>
            ) : null}
          </div>

          <div className='ml-auto flex shrink-0 items-center gap-2'>
            <ThemeModeToggle
              isDark={isDark}
              onToggle={toggleMode}
              className='mb-0 py-0'
            />
            <button
              type='button'
              onClick={() => setNotificationsOpen(true)}
              className={cn(
                'relative flex h-10 w-10 items-center justify-center rounded-xl border shadow-sm transition-colors',
                isDark
                  ? 'border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.08]'
                  : 'border-[#e2e8f0] bg-white hover:border-[#cbd5e1]',
                notifications.unreadCount > 0
                  ? 'text-[var(--brand-primary)]'
                  : isDark
                    ? 'text-text-muted hover:text-text-primary'
                    : 'text-[#64748b] hover:text-[#334155]',
              )}
              aria-label={
                notifications.unreadCount > 0
                  ? `Alertas, ${notifications.unreadCount} não lidas`
                  : 'Alertas'
              }
            >
              <Bell
                size={18}
                strokeWidth={1.5}
                className={cn(
                  notifications.unreadCount > 0 && 'alerts-bell--unread',
                )}
              />
              {notifications.unreadCount > 0 && (
                <span className='absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--brand-primary)] px-1 text-[10px] font-bold text-white'>
                  {notifications.unreadCount > 9 ? '9+' : notifications.unreadCount}
                </span>
              )}
            </button>

            <NotificationsModal
              open={notificationsOpen}
              onClose={() => setNotificationsOpen(false)}
              items={notifications.items}
              loading={notifications.loading}
              unreadCount={notifications.unreadCount}
              onLoad={notifications.loadNotifications}
              onMarkAsRead={notifications.markAsRead}
              onMarkAllAsRead={notifications.markAllAsRead}
            />

            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 text-[11px] font-bold text-white shadow-md',
                isDark ? 'border-white/[0.12] ring-1 ring-white/[0.08]' : 'border-white ring-1 ring-[#e2e8f0]',
              )}
              style={{ backgroundColor: theme.brandPrimary }}
              title={user?.name}
            >
              {user?.avatarUrl ? (
                <img
                  src={resolvePublicAssetUrl(user.avatarUrl)}
                  alt={user.name}
                  className='h-full w-full object-cover'
                />
              ) : (
                initials
              )}
            </div>
          </div>
        </header>

        <section className='min-h-0 flex-1 overflow-y-auto overflow-x-hidden'>
          <div className='mx-auto p-4 pb-20 md:max-w-[1600px] md:p-6 md:pb-6'>
            {isMobile && title ? (
              <h1 className='mb-4 text-base font-bold tracking-tight text-text-primary'>
                {title}
              </h1>
            ) : null}

            {(headerSlot || actions) && (
              <div className='mb-6 flex flex-wrap items-center justify-end gap-2'>
                {headerSlot}
                {actions}
              </div>
            )}

            {children}
          </div>
        </section>
      </main>

      <BottomNavigation />
      <MobileEventLeadCapture />

      <SessionTimeoutWarning
        show={sessionTimeout.showWarning}
        timeLeft={sessionTimeout.timeLeft}
        onExtendSession={sessionTimeout.extendSession}
        onLogout={sessionTimeout.forceLogout}
      />
    </div>
  )
}
